import type { ArchetypeVersion } from "@/lib/admin-api";

import { positionsLine } from "./archetype-form";

// What changed between two versions of one archetype, for the read-only
// history. Pure, so the rules are tested on their own.

export type WeightChange = "added" | "removed" | "changed" | "same";

export interface WeightDiffRow {
  metricKey: string;
  label: string;
  /** null when the metric is not weighted in that version. */
  before: number | null;
  after: number | null;
  change: WeightChange;
}

export interface FieldDiffRow {
  label: string;
  before: string;
  after: string;
}

export interface VersionDiff {
  before: ArchetypeVersion;
  after: ArchetypeVersion;
  /** Only the fields that differ. */
  fields: FieldDiffRow[];
  /** Every metric weighted in either version: the newer version's order, then removed ones. */
  weights: WeightDiffRow[];
  /** True when the definitions are the same (notes aside). */
  unchanged: boolean;
  /** True when the notes differ. Notes are not part of the definition. */
  notesChanged: boolean;
}

function yesNo(value: boolean): string {
  return value ? "Yes" : "No";
}

/** The version before `version` in a newest-first list, or null for the first one. */
export function previousVersion(
  versions: readonly ArchetypeVersion[],
  version: number,
): ArchetypeVersion | null {
  let best: ArchetypeVersion | null = null;
  for (const v of versions) {
    if (v.version < version && (!best || v.version > best.version)) best = v;
  }
  return best;
}

/** Versions that have one before them, newest first: the pairs the history can compare. */
export function comparableVersions(versions: readonly ArchetypeVersion[]): ArchetypeVersion[] {
  return [...versions]
    .sort((a, b) => b.version - a.version)
    .filter((v) => previousVersion(versions, v.version) !== null);
}

export function diffVersions(before: ArchetypeVersion, after: ArchetypeVersion): VersionDiff {
  const fields: FieldDiffRow[] = [];
  if (before.name !== after.name) {
    fields.push({ label: "Name", before: before.name, after: after.name });
  }
  const homesBefore = positionsLine(before.homePositions);
  const homesAfter = positionsLine(after.homePositions);
  if (homesBefore !== homesAfter) {
    fields.push({ label: "Home positions", before: homesBefore, after: homesAfter });
  }
  if (before.positional !== after.positional) {
    fields.push({
      label: "Positional",
      before: yesNo(before.positional),
      after: yesNo(after.positional),
    });
  }
  const eventBefore = before.requiredData === "event";
  const eventAfter = after.requiredData === "event";
  if (eventBefore !== eventAfter) {
    fields.push({
      label: "Event data only",
      before: yesNo(eventBefore),
      after: yesNo(eventAfter),
    });
  }

  const beforeWeights = new Map(before.weights.map((w) => [w.metricKey, w]));
  const afterKeys = new Set(after.weights.map((w) => w.metricKey));
  const weights: WeightDiffRow[] = after.weights.map((w) => {
    const old = beforeWeights.get(w.metricKey);
    const change: WeightChange = !old ? "added" : old.weight === w.weight ? "same" : "changed";
    return {
      metricKey: w.metricKey,
      label: w.label || old?.label || w.metricKey,
      before: old ? old.weight : null,
      after: w.weight,
      change,
    };
  });
  for (const w of before.weights) {
    if (afterKeys.has(w.metricKey)) continue;
    weights.push({
      metricKey: w.metricKey,
      label: w.label || w.metricKey,
      before: w.weight,
      after: null,
      change: "removed",
    });
  }

  return {
    before,
    after,
    fields,
    weights,
    unchanged: fields.length === 0 && weights.every((w) => w.change === "same"),
    notesChanged: (before.notes ?? "") !== (after.notes ?? ""),
  };
}

/** "3", "2.5". Weights are halves, so at most one decimal. */
export function formatWeight(weight: number | null): string {
  if (weight == null) return "";
  return Number.isInteger(weight) ? String(weight) : weight.toFixed(1);
}
